import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Typography, Grid, IconButton, Tooltip, Button} from '@mui/material'
import {makeStyles} from '@oc/styles'
import clsx from 'clsx'
import {ArrowIcon} from '@oc/icons'
import {NoScrollWhenLoading, actionBarHeight} from './Layout'
import {messages} from './texts'

type MuiButtonColor = 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'

interface OcSimpleLayoutAction {
    onClick: () => void
    label: string
    disabled?: boolean
    className?: string
    hide?: boolean
    key: string
    startIcon?: React.ReactNode
    color?: MuiButtonColor
}

interface OcSimpleLayoutProps {
    actions?: OcSimpleLayoutAction[]
    children?: React.ReactNode
    header?: string
    windowTitle?: string
    headerAction?: () => void
    headerIcon?: JSX.Element
    headerActionTooltip?: string
    overrideWrapperClassName?: string
}

const useLayoutStyles = makeStyles(theme => ({
    header: {
        padding: `0px ${theme.spacing(2)}px`
    },
    headerActionButton: {
        padding: '0px'
    },
    wrapper: {
        overflow: 'auto',
        maxHeight: '100%',
        padding: `${theme.spacing(2)}px`
    },
    root: {
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
        position: 'relative'
    },
    content: {
        height: '100%',
        flexWrap: 'nowrap'
    },
    contentWithAction: {
        height: `calc(100% - ${actionBarHeight}px)`
    }
}))

const useActionStyles = makeStyles(theme => ({
    bottomNavigation: {
        height: `${actionBarHeight}px`,
        maxHeight: `${actionBarHeight}px`,
        display: 'flex',
        backgroundColor: theme.palette.background.paper,
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 'auto',
        maxWidth: 'calc(100vw - 240px)',
        transition: 'max-width 1s',
        boxShadow: theme.shadows[6],
        margin: theme.spacing(0, 2, 2, 0),
        zIndex: 100,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0)
    },
    buttonClass: {
        width: '150px'
    }
}))

function _OcSimpleLayoutActions(props: {actions?: OcSimpleLayoutAction[]}) {
    const {actions} = props
    const {
        classes: {bottomNavigation, buttonClass}
    } = useActionStyles()

    return (
        <div id="navigation-action-bar-simple" className={bottomNavigation}>
            {actions
                ?.filter(action => action.hide !== true)
                .map(action => {
                    return (
                        <Button
                            key={action.key}
                            startIcon={action.startIcon}
                            disabled={action.disabled}
                            onClick={action.onClick}
                            color={action.color || 'primary'}
                            variant="contained"
                            className={action.className ? clsx(buttonClass, action.className) : buttonClass}
                        >
                            {action.label}
                        </Button>
                    )
                })}
        </div>
    )
}
const OcSimpleLayoutActions = observer(_OcSimpleLayoutActions)

function _OcSimpleLayout(props: OcSimpleLayoutProps): JSX.Element {
    const {
        children,
        header,
        headerAction,
        headerIcon,
        headerActionTooltip,
        windowTitle,
        actions,
        overrideWrapperClassName
    } = props
    const {
        classes: {wrapper, contentWithAction, content, header: headerClass, headerActionButton}
    } = useLayoutStyles()

    React.useEffect(
        function () {
            if (windowTitle || header) {
                document.title = messages.getText('windowTitle', 'withViewName', windowTitle ?? header)
            } else {
                document.title = messages.getText('windowTitle', 'withoutViewName')
            }
        },
        [windowTitle, header]
    )

    return (
        <NoScrollWhenLoading>
            <Grid
                container
                className={(actions ? actions.length : 0) > 0 ? clsx(content, contentWithAction) : content}
                direction="column"
            >
                {typeof header !== 'undefined' ? (
                    <Typography variant="h1" className={headerClass}>
                        {headerAction ? (
                            <Tooltip title={headerActionTooltip || ''}>
                                <IconButton onClick={headerAction} color="inherit" className={headerActionButton}>
                                    {headerIcon ? (
                                        headerIcon
                                    ) : (
                                        <ArrowIcon fontSize="inherit" rotate="left" color="inherit" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {header}
                    </Typography>
                ) : null}

                <Grid xs={12} className={overrideWrapperClassName ? overrideWrapperClassName : wrapper} item>
                    {children || null}
                </Grid>
            </Grid>

            <OcSimpleLayoutActions actions={actions} />
        </NoScrollWhenLoading>
    )
}

/**
 * Simple layout - styling is copied from `Layout` component.
 * This component could be used, if the view is not using any advanced functions, like `ValidationState` or `GlobalSaveDiscard` ...
 */
export const OcSimpleLayout = observer(_OcSimpleLayout)
